/**
 * The client page's Mockups tab (session 48): every mockup shared with this client, where
 * its newest version's review stands, how many comments are still open — and New mockup.
 *
 * A row opens `/studio/mockups/<ref>`, the viewer, which is where everything else is done.
 * Archived ones are folded away rather than hidden, because "what did we show them in
 * March" is a question the studio asks.
 */

import { Link, useNavigate } from '@tanstack/react-router'

import type { MockupView } from '@fareast/shared'

import { useStudioRead } from '../../../api/studio.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { FileRow } from '../../../components/portal/Files.js'
import { Row, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Pill, Tag } from '../../../components/portal/Status.js'
import { DateText, Linkish, Muted, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { UploadMockupButton } from '../../mockups/UploadMockupButton.js'
import { reviewPill, versionWord } from '../../mockups/words.js'

export function MockupsSection({ slug }: { readonly slug: string }) {
  const read = useStudioRead('mockups_list', { client: slug, include_archived: true })
  const navigate = useNavigate()
  const format = useFormat()

  const all = read.data?.mockups ?? []
  const live = all.filter((one) => one.archived_at === null)
  const archived = all.filter((one) => one.archived_at !== null)

  return (
    <Section>
      <SectionHead title="Mockups" note={format.number(live.length)} />
      <Stack>
        <Row>
          <UploadMockupButton
            target={{ kind: 'new', client: slug }}
            onDone={(ref) => {
              void navigate({ to: '/studio/mockups/$ref', params: { ref } })
            }}
          />
          <Small>
            <Muted>
              HTML designs the client views in the portal and answers — a link anyone can open,
              never indexed.
            </Muted>
          </Small>
        </Row>

        {read.data === undefined ? (
          <Empty>Loading the mockups&hellip;</Empty>
        ) : live.length === 0 ? (
          <Empty>No mockups shared with this client yet.</Empty>
        ) : (
          <Card>
            <CardBody tight>
              {live.map((one) => (
                <MockupRow key={one.ref} mockup={one} />
              ))}
            </CardBody>
          </Card>
        )}

        {archived.length === 0 ? null : (
          <Fold summary={`Archived — ${format.number(archived.length)}`}>
            <Card>
              <CardBody tight>
                {archived.map((one) => (
                  <MockupRow key={one.ref} mockup={one} />
                ))}
              </CardBody>
            </Card>
          </Fold>
        )}
      </Stack>
    </Section>
  )
}

function MockupRow({ mockup }: { readonly mockup: MockupView }) {
  const format = useFormat()
  const pill = mockup.latest === null ? null : reviewPill(mockup.latest.review.state, 'studio')
  return (
    <FileRow
      icon="html"
      title={
        <Linkish asChild strong>
          <Link to="/studio/mockups/$ref" params={{ ref: mockup.ref }}>
            {mockup.title}
          </Link>
        </Linkish>
      }
      sub={`${mockup.ref} · ${mockup.latest === null ? 'nothing uploaded yet' : versionWord(mockup.latest)}`}
      meta={
        <>
          {mockup.open_comments === 0 ? null : (
            <Tag>
              {format.number(mockup.open_comments)} open comment
              {mockup.open_comments === 1 ? '' : 's'}
            </Tag>
          )}
          {pill === null ? null : <Pill state={pill.need ? 'need' : undefined}>{pill.word}</Pill>}
          <DateText value={mockup.updated_at} />
        </>
      }
    />
  )
}
